import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { useContext } from 'react';
import useStyles from './CustomProductBadge.styles';
import Image from '../Image/Image';
import { processImageUrl } from '../../utils/image';
import { useTextColor } from '../../styles/jss/utils';
import { SiteContext } from '../../utils/common';
import { BADGE_VARIANTS } from '../../utils/constants';
import { spacing } from '../../styles/jss/variables';
// Applied as inline styles rather than CSS classes so the badge is positioned correctly on its
// very first paint, instead of waiting for tss-react to asynchronously insert a position class
const POSITION_STYLES = {
    topLeft: { top: 0, left: 0, right: 'auto', bottom: 'auto', alignItems: 'flex-start', paddingTop: spacing.xs },
    topRight: { top: 0, right: 0, left: 'auto', bottom: 'auto', alignItems: 'flex-end', paddingTop: spacing.xs },
    bottomLeft: { bottom: 0, left: 0, top: 'auto', right: 'auto', alignItems: 'flex-start', paddingBottom: spacing.xs },
    bottomRight: { bottom: 0, right: 0, top: 'auto', left: 'auto', alignItems: 'flex-end', paddingBottom: spacing.xs },
};
const CustomProductBadge = (props) => {
    const { badgeImagePosition, badgeTextPosition, variant = BADGE_VARIANTS.TEXT_ONLY, backgroundColor: propBackgroundColor, textColor: propTextColor, badgeTextAlignment: providedBadgeTextAlignment, badgeText, imageSrc, imageAlt = '', isCarousel = false, site, badgeStyle, isFromProductCard = false, isFromProductDetail = false, isFromProductDetailV2 = false, } = props;
    const { classes: styleClasses } = useStyles({ isFromProductDetail, isFromProductDetailV2 });
    const { isFlyingFlowers } = useContext(SiteContext);
    // Determine badge type
    const isFFRounded = site === 'Flying Flowers' && badgeStyle === 'Rounded';
    const isFFSquare = site === 'Flying Flowers' && badgeStyle === 'Square block';
    const isFFBadge = isFFRounded || isFFSquare;
    // Determine default positions
    const defaultTextPosition = isFFBadge ? 'bottomRight' : 'topLeft';
    const finalTextPosition = badgeTextPosition || defaultTextPosition;
    const finalImagePosition = badgeImagePosition || 'topLeft';
    // Determine text alignment
    const badgeTextAlignment = providedBadgeTextAlignment || (isFFRounded ? 'CENTER' : 'LEFT');
    // Get colors
    const { textColor: hookTextColor, backgroundColor: hookBackgroundColor } = useTextColor(propBackgroundColor);
    const finalTextColor = propTextColor || hookTextColor;
    const finalBackgroundColor = hookBackgroundColor || '#FFFFFF';
    // Setup styles
    const containerStyles = {};
    const textWrapperBaseStyles = finalBackgroundColor ? { backgroundColor: finalBackgroundColor } : {};
    const textAlignmentMap = {
        LEFT: 'left',
        CENTER: 'center',
        RIGHT: 'right',
    };
    const textAlignment = textAlignmentMap[badgeTextAlignment] || 'left';
    const textStyles = Object.assign(Object.assign({}, (finalTextColor && { color: finalTextColor })), { textAlign: textAlignment });
    const finalTextWrapperStyles = Object.assign(Object.assign({}, textWrapperBaseStyles), { textAlign: textAlignment });
    // Get carousel padding
    const getCarouselPaddingClass = (position) => {
        if (!isCarousel || isFromProductCard || isFlyingFlowers || isFromProductDetailV2)
            return '';
        const isLeftAlign = position === 'topLeft' || position === 'bottomLeft';
        return isLeftAlign ? styleClasses.carouselPaddingLeft : styleClasses.carouselPaddingRight;
    };
    // Get style classes
    const getTextClass = () => (isFromProductCard ? styleClasses.textProductCard : styleClasses.text);
    const getFFRoundedTextClass = () => isFromProductCard ? styleClasses.ffRoundedTextProductCard : styleClasses.ffRoundedText;
    const getFFSquareTextClass = () => isFromProductCard ? styleClasses.ffSquareTextProductCard : styleClasses.ffSquareText;
    const getFFRoundedBadgeClass = () => isFromProductCard ? styleClasses.ffRoundedBadgeProductCard : styleClasses.ffRoundedBadge;
    // Returns variant-specific class only for imageOnly variant
    const getVariantClass = (variantName) => {
        if (variantName === BADGE_VARIANTS.IMAGE_ONLY) {
            return isFromProductCard ? styleClasses.imageOnlyProductCard : styleClasses.imageOnly;
        }
        return '';
    };
    // Get container classes
    const getContainerClasses = () => {
        // For imageOnly variant, use image position; otherwise use text position
        const positionToUse = variant === BADGE_VARIANTS.IMAGE_ONLY ? finalImagePosition : finalTextPosition;
        const classes = [styleClasses.container, styleClasses[positionToUse]];
        if (!isFFBadge) {
            classes.push(getVariantClass(variant));
        }
        if (isFFRounded) {
            classes.push(isFromProductCard ? styleClasses.ffRoundedContainerProductCard : styleClasses.ffRoundedContainer);
        }
        classes.push(getCarouselPaddingClass(positionToUse));
        return classes.filter(Boolean);
    };
    // Render helpers
    const renderFFRoundedContent = () => (_jsx("div", { className: getFFRoundedBadgeClass(), style: { backgroundColor: finalBackgroundColor }, children: _jsx("span", { className: clsx(getFFRoundedTextClass()), style: { color: finalTextColor }, children: badgeText }) }));
    const getImageWrapperDetailClass = () => {
        if (isFromProductDetail)
            return styleClasses.productDetailImageWrapper;
        return undefined;
    };
    const imageWrapperDetailClass = getImageWrapperDetailClass();
    // Apply responsive width class only for ProductDetailImages badges (not product card)
    const getImageWrapperWidthClass = () => {
        if (isFromProductCard)
            return styleClasses.imageWrapperProductCard; // Product card: fixed 72px
        return styleClasses.imageWrapperResponsive; // ProductDetailImages: responsive widths
    };
    const imageWrapperWidthClass = getImageWrapperWidthClass();
    const renderFFSquareContent = () => (_jsxs(_Fragment, { children: [badgeText && (_jsx("div", { className: styleClasses.textWrapper, style: { backgroundColor: finalBackgroundColor }, children: _jsx("span", { className: clsx(getFFSquareTextClass()), style: { color: finalTextColor }, children: badgeText }) })), imageSrc && (_jsx("div", { className: clsx(styleClasses.imageWrapper, imageWrapperDetailClass, imageWrapperWidthClass), children: _jsx(Image, { imageUrl: imageSrc.startsWith('http') ? processImageUrl(imageSrc, { w: 268 }) : imageSrc, altText: imageAlt, className: styleClasses.image, width: "268", height: "268", loading: "lazy", useBlurPlaceholder: false }) }))] }));
    const renderTextOnly = () => badgeText && (_jsx("div", { className: styleClasses.textWrapper, style: finalTextWrapperStyles, children: _jsx("span", { className: clsx(getTextClass()), style: textStyles, children: badgeText }) }));
    const renderTextWithImage = () => (_jsxs(_Fragment, { children: [badgeText && (_jsx("div", { className: styleClasses.textWrapper, style: finalTextWrapperStyles, children: _jsx("span", { className: clsx(getTextClass()), style: textStyles, children: badgeText }) })), imageSrc && (_jsx("div", { className: clsx(styleClasses.imageWrapper, imageWrapperDetailClass, imageWrapperWidthClass), children: _jsx(Image, { imageUrl: imageSrc.startsWith('http') ? processImageUrl(imageSrc, { w: 268 }) : imageSrc, altText: imageAlt, className: styleClasses.image, width: "268", height: "268", loading: "lazy", useBlurPlaceholder: false }) }))] }));
    const renderImageOnly = () => imageSrc && (_jsx("div", { className: clsx(styleClasses.imageWrapper, imageWrapperDetailClass, imageWrapperWidthClass), children: _jsx(Image, { imageUrl: imageSrc.startsWith('http') ? processImageUrl(imageSrc, { w: 268 }) : imageSrc, altText: imageAlt || 'Badge Image', className: styleClasses.image, width: "268", height: "268", loading: "lazy", useBlurPlaceholder: false }) }));
    const renderContent = () => {
        if (isFFRounded && badgeText)
            return renderFFRoundedContent();
        if (isFFSquare)
            return renderFFSquareContent();
        // For standard badges, render text and image in separate containers if they have different positions
        const textElement = variant === BADGE_VARIANTS.IMAGE_ONLY ? null : renderTextOnly();
        const imageElement = variant === BADGE_VARIANTS.TEXT_ONLY ? null : renderImageOnly();
        // If both elements exist and have different positions, render them separately
        if (textElement && imageElement && badgeTextPosition !== badgeImagePosition) {
            return (_jsxs(_Fragment, { children: [textElement, imageElement] }));
        }
        // Otherwise use the original combined approach
        const variantMap = {
            textOnly: renderTextOnly,
            textWithImage: renderTextWithImage,
            imageOnly: renderImageOnly,
        };
        return variantMap[variant]();
    };
    // Helper function to get container classes for a specific position
    const getPositionContainerClasses = (position) => {
        const classes = [styleClasses.container, styleClasses[position]];
        if (!isFFBadge) {
            classes.push(getVariantClass(variant));
        }
        if (isFFRounded) {
            classes.push(isFromProductCard ? styleClasses.ffRoundedContainerProductCard : styleClasses.ffRoundedContainer);
        }
        // Use appropriate position for carousel padding logic
        if (position === finalTextPosition || position === finalImagePosition) {
            classes.push(getCarouselPaddingClass(position));
        }
        return classes.filter(Boolean);
    };
    // Check if text and image should be rendered in separate containers
    const shouldRenderSeparately = () => {
        if (variant === BADGE_VARIANTS.TEXT_ONLY || variant === BADGE_VARIANTS.IMAGE_ONLY)
            return false; // Single element
        return finalTextPosition !== finalImagePosition; // Different positions
    };
    if (shouldRenderSeparately()) {
        // Render text and image in separate containers
        return (_jsxs(_Fragment, { children: [badgeText && (_jsx("div", { className: clsx(...getPositionContainerClasses(finalTextPosition)), style: Object.assign(Object.assign({}, POSITION_STYLES[finalTextPosition]), containerStyles), children: _jsx("div", { className: styleClasses.textWrapper, style: finalTextWrapperStyles, children: _jsx("span", { className: clsx(getTextClass()), style: textStyles, children: badgeText }) }) })), imageSrc && (_jsx("div", { className: clsx(...getPositionContainerClasses(finalImagePosition)), style: Object.assign(Object.assign({}, POSITION_STYLES[finalImagePosition]), containerStyles), children: _jsx("div", { className: clsx(styleClasses.imageWrapper, imageWrapperDetailClass, imageWrapperWidthClass), children: _jsx(Image, { imageUrl: imageSrc.startsWith('http') ? processImageUrl(imageSrc, { w: 268 }) : imageSrc, altText: imageAlt || 'Badge Image', className: styleClasses.image, width: "268", height: "268", loading: "lazy", useBlurPlaceholder: false }) }) }))] }));
    }
    // Original single container approach for FF badges or when positions are the same
    const singleContainerPosition = variant === BADGE_VARIANTS.IMAGE_ONLY ? finalImagePosition : finalTextPosition;
    return (_jsx("div", { className: clsx(...getContainerClasses()), style: Object.assign(Object.assign({}, POSITION_STYLES[singleContainerPosition]), containerStyles), children: renderContent() }));
};
export default CustomProductBadge;
